import { useMemo } from 'react';
import { useAppStore } from '../stores/appStore';

// Default feature states used before API loads.
// Reflects the intended defaults for new tenants (mentions/notifications/financials off;
// all Views-section entries, including the Timeline/List toggles, on).
const DEFAULT_FEATURES: Record<string, boolean> = {
  viewTimeline:              true,
  viewList:                  true,
  viewKanban:                true,
  viewCalendar:              true,
  batchUpdate:               true,
  scoring:                   true,
  savedViews:                true,
  dependencies:              true,
  comments:                  true,
  mentions:                  false,
  notifications:             false,
  auditLog:                  true,
  financials:                false,
  chartsWithFilters:         true,
  recentActivity:            true,
  recentComments:            true,
  intelligence:              true,
  intelligenceCreate:        true,
  intelligenceItemInsights:  true,
  intelligenceHealth:        false,
  intelligenceAsk:           false,
  intelligenceDigest:        false,
  jira:                      false,
};

export function useLabsFeatures() {
  const labsFeatures = useAppStore((s) => s.labsFeatures);

  return useMemo(() => {
    // Build lookup maps with defaults as base
    const enabledMap = new Map<string, boolean>();
    const parentMap = new Map<string, string>(); // featureKey → parentFeatureKey

    for (const [key, enabled] of Object.entries(DEFAULT_FEATURES)) {
      enabledMap.set(key, enabled);
    }
    // Overlay with actual configs from API
    for (const feature of labsFeatures) {
      enabledMap.set(feature.featureKey, feature.isEnabled);
      if (feature.parentFeatureKey) {
        parentMap.set(feature.featureKey, feature.parentFeatureKey);
      }
    }

    return {
      /**
       * Check if a feature is enabled.
       * Sub-features require BOTH their parent AND themselves to be enabled.
       */
      isEnabled: (key: string): boolean => {
        const parentKey = parentMap.get(key);
        if (parentKey) {
          return (enabledMap.get(parentKey) ?? true) && (enabledMap.get(key) ?? true);
        }
        return enabledMap.get(key) ?? true;
      },
      /** Get all feature configs (for Settings UI) */
      all: () => labsFeatures,
    };
  }, [labsFeatures]);
}
